import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";

const LAST_UPDATED = "May 22, 2026";
const EFFECTIVE_DATE = "May 22, 2026";
const COMPANY = "BerryByte Innovations";
const WEBSITE = "https://www.berrybyteinn.com";
const EMAIL = "sales@berrybyteinn.com";
const PHONE = "+1 201-846-9830";

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
      <span className="w-1 h-6 rounded-full bg-gradient-to-b from-violet-500 to-cyan-500 flex-shrink-0" />
      {title}
    </h2>
    <div className="text-slate-400 leading-relaxed space-y-3 pl-4">{children}</div>
  </section>
);

const P = ({ children }) => <p className="text-slate-400 leading-relaxed">{children}</p>;

const Highlight = ({ children }) => (
  <div className="my-4 p-5 rounded-xl border border-emerald-500/30 bg-emerald-500/5">
    <div className="flex items-start gap-3">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5">
        <path d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-.529 3.78 3.745 3.745 0 0 1-3.780.53A3.745 3.745 0 0 1 12 21a3.745 3.745 0 0 1-3.098-1.622 3.745 3.745 0 0 1-3.78-.53 3.745 3.745 0 0 1-.53-3.78A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 .53-3.78 3.745 3.745 0 0 1 3.78-.53A3.745 3.745 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.745 3.745 0 0 1 3.78.53 3.745 3.745 0 0 1 .53 3.78A3.745 3.745 0 0 1 21 12z" />
      </svg>
      <p className="text-emerald-300 font-medium leading-relaxed">{children}</p>
    </div>
  </div>
);

const UL = ({ items }) => (
  <ul className="space-y-2 pl-2">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-slate-400">
        <span className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0 mt-2" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const TOC = [
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "how-we-use", label: "How We Use Your Information" },
  { id: "no-sale", label: "We Do Not Sell Your Information" },
  { id: "sms-mobile", label: "SMS & Mobile Phone Number Policy (10DLC)" },
  { id: "communications", label: "Communications & Contact Methods" },
  { id: "data-sharing", label: "Data Sharing & Disclosure" },
  { id: "cookies", label: "Cookies & Tracking" },
  { id: "third-party", label: "Third-Party Services" },
  { id: "data-security", label: "Data Security" },
  { id: "data-retention", label: "Data Retention" },
  { id: "your-rights", label: "Your Privacy Rights" },
  { id: "california", label: "California Residents (CCPA)" },
  { id: "canada", label: "Canadian Residents (PIPEDA)" },
  { id: "children", label: "Children's Privacy" },
  { id: "changes", label: "Changes to This Policy" },
  { id: "contact", label: "Contact Us" },
];

export default function PrivacyPolicy() {
  useSEO({
    title: "Privacy Policy | BerryByte Innovations",
    description: "BerryByte Innovations Privacy Policy. We do not sell or share your personal information. Learn how we collect, use, and protect your data.",
    canonical: "https://www.berrybyteinn.com/privacy-policy",
  });

  return (
    <SharedLayout>
      <div className="relative pt-32 pb-20 bg-[#050810]">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-sm font-medium mb-6">
              Legal Document
            </div>
            <h1 className="text-4xl lg:text-5xl font-black text-white mb-4">Privacy Policy</h1>
            <div className="flex flex-wrap gap-6 text-sm text-slate-500">
              <span>Effective Date: {EFFECTIVE_DATE}</span>
              <span>Last Updated: {LAST_UPDATED}</span>
            </div>
            <div className="mt-6 p-4 rounded-xl border border-white/8 bg-white/[0.02]">
              <P>
                This Privacy Policy describes how {COMPANY} ("Company," "we," "us," or "our") collects,
                uses, and protects the personal information of visitors and clients who access our website
                at {WEBSITE} or engage with our services. By using our website or services, you agree
                to the practices described in this policy.
              </P>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="mb-12 p-6 rounded-2xl border border-white/8 bg-white/[0.02]">
            <h2 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Table of Contents</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {TOC.map(({ id, label }, i) => (
                <a key={id} href={`#${id}`} className="flex items-center gap-2 text-sm text-slate-400 hover:text-violet-400 transition-colors py-1">
                  <span className="text-violet-500/60 font-mono text-xs w-5">{String(i + 1).padStart(2, "0")}.</span>
                  {label}
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          <Section id="information-we-collect" title="1. Information We Collect">
            <P>We collect information in the following ways:</P>
            <p className="font-semibold text-slate-300 mt-3">Information You Provide Directly</p>
            <UL items={[
              "Contact form submissions: name, email address, phone number, company name, and project details",
              "Email correspondence and direct communications with our team",
              "Business information provided during project onboarding or client agreements",
              "Payment and billing information when you engage our services (processed securely via third-party payment processors)",
              "Information submitted through our BerryChat CRM platform (chat messages, support tickets, contact details)",
            ]} />
            <p className="font-semibold text-slate-300 mt-4">Information Collected Automatically</p>
            <UL items={[
              "IP address, browser type, operating system, and device information",
              "Pages visited, time spent on pages, referral URLs, and click activity",
              "Cookies and similar tracking technologies (see Section 6)",
              "Log data from our servers when you access our website or services",
            ]} />
          </Section>

          <Section id="how-we-use" title="2. How We Use Your Information">
            <P>We use the information we collect for the following purposes:</P>
            <UL items={[
              "To respond to your inquiries, project requests, and support tickets",
              "To deliver, manage, and improve our software development services",
              "To communicate with you about your project, proposals, and deliverables",
              "To send you service-related updates, invoices, and project notifications",
              "To improve our website, services, and user experience",
              "To comply with applicable legal obligations",
              "To prevent fraud, protect security, and enforce our agreements",
              "To analyze aggregate, anonymized usage data to improve our offerings",
            ]} />
            <P>We will not use your information for purposes incompatible with those listed above without first notifying you and, where required, obtaining your consent.</P>
          </Section>

          <Section id="no-sale" title="3. We Do Not Sell Your Information">
            <Highlight>
              BerryByte Innovations does not sell, rent, trade, or otherwise transfer your personal
              information to third parties for monetary or other valuable consideration. We do not share
              your personal data with data brokers, advertising networks, or any commercial entity for
              the purpose of targeted advertising or marketing on their behalf. Your information is yours —
              we use it only to serve you.
            </Highlight>
            <P>
              This commitment applies to all personal information you provide to us, including but not
              limited to names, email addresses, phone numbers, business information, and any other
              data you share with BerryByte Innovations through our website, services, or direct communications.
            </P>
            <P>
              We do not and will not sell your personal information to any third party, including under
              the California Consumer Privacy Act (CCPA) definition of "sale." We have not sold personal
              information in the preceding twelve (12) months.
            </P>
          </Section>

          {/* ── 10DLC COMPLIANCE SECTION ── */}
          <section id="sms-mobile" className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-1 h-6 rounded-full bg-gradient-to-b from-violet-500 to-cyan-500 flex-shrink-0" />
              4. SMS &amp; Mobile Phone Number Policy
            </h2>

            {/* 10DLC compliance callout — high-visibility box */}
            <div className="mb-6 rounded-2xl border-2 border-violet-500/40 bg-violet-500/8 p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5 text-violet-400">
                    <path d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-bold text-lg mb-2">
                    We Do Not Share Your Mobile Phone Number
                  </p>
                  <p className="text-slate-300 leading-relaxed text-base">
                    <strong className="text-white">BerryByte Innovations does not sell, rent, share, or
                    disclose your mobile phone number to any third party for any purpose.</strong> Mobile
                    phone numbers collected through our website, contact forms, or during client
                    engagements are used solely to communicate with you directly. Your mobile number
                    is never transferred to data brokers, marketing platforms, advertising networks,
                    or any other third-party organization.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-slate-400 leading-relaxed space-y-4 pl-4">
              <p className="text-slate-300 font-semibold">SMS / Text Message Communications</p>
              <p>
                By providing your mobile phone number to BerryByte Innovations, you may receive
                SMS (text message) communications from us related to your project, appointment
                reminders, or service updates. The following terms govern our SMS communications:
              </p>

              <ul className="space-y-3 pl-2">
                {[
                  { label: "Consent Required", text: "We will only send you SMS messages if you have expressly consented to receive them, either by checking a consent box, providing your number in a context where SMS communication is indicated, or by texting us first." },
                  { label: "Message Types", text: "SMS messages from BerryByte Innovations are limited to: project status updates, appointment/meeting reminders, time-sensitive service notifications, and responses to your direct inquiries. We do not send promotional or marketing SMS blasts." },
                  { label: "Message Frequency", text: "Message frequency varies depending on your active engagement with our team. Typically no more than 5 messages per month during an active project." },
                  { label: "Carrier Rates", text: "Message and data rates may apply depending on your mobile carrier plan. BerryByte Innovations is not responsible for any charges imposed by your carrier." },
                  { label: "Opt-Out at Any Time", text: "You can stop receiving SMS messages at any time by replying STOP to any message we send. After opting out, you will receive one final confirmation message and no further SMS will be sent." },
                  { label: "Help", text: "For assistance, reply HELP to any SMS or contact us at sales@berrybyteinn.com or +1 201-846-9830." },
                ].map(({ label, text }) => (
                  <li key={label} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0 mt-2.5" />
                    <span><strong className="text-slate-300">{label}:</strong> {text}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-5 p-5 rounded-xl border border-white/10 bg-white/[0.02]">
                <p className="text-white font-semibold mb-2">
                  Mobile Phone Number — Third-Party Sharing Policy
                </p>
                <p className="text-slate-300 leading-relaxed">
                  Mobile phone numbers and SMS consent obtained by BerryByte Innovations will
                  <strong className="text-white"> not be shared with, sold to, or transferred to any
                  third party</strong> under any circumstances, including but not limited to:
                  marketing companies, data aggregators, lead generation services, advertisers,
                  affiliate networks, or any other commercial entity. This policy is maintained in
                  strict compliance with applicable telecommunications laws and 10DLC carrier
                  registration requirements.
                </p>
              </div>

              <p className="text-slate-400 text-sm mt-3">
                Our SMS program is registered with US carriers under the 10DLC (10-Digit Long Code)
                framework. All messages are sent through registered, compliant channels. If you have
                any concerns about SMS communications you have received from us, please contact us
                immediately at {EMAIL}.
              </p>
            </div>
          </section>
          {/* ── END 10DLC SECTION ── */}

          <Section id="communications" title="5. Communications & Contact Methods">
            <P>
              When you contact us or engage our services, we may communicate with you using the following
              methods. By providing your contact information and engaging with our team, you consent to
              being contacted through the channels you have shared with us.
            </P>

            <p className="font-semibold text-slate-300 mt-4">Email</p>
            <P>We use email as our primary method of written communication for project updates, proposals, invoices, and general correspondence. You may opt out of non-essential marketing emails at any time by clicking "unsubscribe" in any such email or by contacting us directly.</P>

            <p className="font-semibold text-slate-300 mt-4">Telephone (Voice Calls)</p>
            <P>We may contact you by phone at the number you provide for project discussions, consultations, and support. You can request that we communicate with you by a different method at any time.</P>

            <p className="font-semibold text-slate-300 mt-4">Zoom (Video & Phone Calls)</p>
            <P>
              We use Zoom for video conferencing and Zoom Phone for voice communications with clients and
              prospects. Zoom meetings may be recorded for internal quality and project documentation
              purposes. We will notify you before recording any session and obtain your consent. Zoom
              processes communication data in accordance with its own Privacy Policy (zoom.us/privacy).
            </P>

            <p className="font-semibold text-slate-300 mt-4">SMS / Text Messages</p>
            <P>
              With your explicit consent, we may send you text messages (SMS) for appointment reminders,
              project status updates, or other service-related communications. We do not send unsolicited
              marketing SMS messages. Standard messaging rates from your carrier may apply. You may opt
              out of SMS communications at any time by replying STOP to any text message we send, or by
              contacting us at {EMAIL}.
            </P>

            <p className="font-semibold text-slate-300 mt-4">Live Chat (BerryChat Widget)</p>
            <P>Our website includes a live chat widget powered by BerryChat, our own customer support platform. Chat conversations are stored securely and used to respond to your inquiries and improve our support quality. We do not share chat data with third parties for marketing purposes.</P>
          </Section>

          <Section id="data-sharing" title="6. Data Sharing & Disclosure">
            <P>We do not sell your personal information. We may share your data only in the following limited circumstances:</P>
            <UL items={[
              "Service Providers: Trusted vendors who assist us in operating our website and delivering services (e.g., cloud hosting, payment processing, project management tools). These providers are contractually bound to use your data only for the purposes we specify and to protect it appropriately.",
              "Legal Requirements: If required by law, subpoena, court order, or governmental authority, we may disclose information as necessary to comply with applicable legal obligations.",
              "Business Protection: To protect the rights, property, or safety of BerryByte Innovations, our clients, or the public as permitted by law.",
              "Business Transfer: In the event of a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you via email or a prominent notice on our website prior to such a transfer.",
              "With Your Consent: We may share your information with third parties when you have explicitly consented to such sharing.",
            ]} />
            <P>In all cases, we share only the minimum information necessary and require all recipients to maintain appropriate data protection standards.</P>
          </Section>

          <Section id="cookies" title="7. Cookies & Tracking Technologies">
            <P>Our website uses cookies and similar tracking technologies to enhance your experience and analyze site usage. Cookies are small data files placed on your device.</P>
            <p className="font-semibold text-slate-300 mt-3">Types of Cookies We Use</p>
            <UL items={[
              "Essential Cookies: Required for the website to function properly (session management, security). These cannot be disabled.",
              "Analytics Cookies: Help us understand how visitors interact with our site (page views, navigation paths). Data is aggregated and anonymized.",
              "Preference Cookies: Remember your settings and choices to improve your experience on return visits.",
            ]} />
            <P>You can control or disable cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of our website. We do not use cookies to track you across unaffiliated third-party websites for advertising purposes.</P>
          </Section>

          <Section id="third-party" title="8. Third-Party Services">
            <P>Our services and website integrate with select third-party platforms to deliver our work. Each of these services has its own privacy policy governing their data practices:</P>
            <UL items={[
              "Zoom / Zoom Phone — Video conferencing and voice communications (zoom.us/privacy)",
              "Google Workspace — Email, documents, and collaboration tools",
              "GitHub — Source code repositories and version control",
              "AWS / Google Cloud / Microsoft Azure — Cloud infrastructure and hosting",
              "Stripe — Payment processing (stripe.com/privacy)",
              "Zapier / Slack — Workflow automations and team communications",
            ]} />
            <P>We are not responsible for the privacy practices of these third-party services. We encourage you to review their policies. We select third-party providers who maintain appropriate security and privacy standards.</P>
          </Section>

          <Section id="data-security" title="9. Data Security">
            <P>We implement commercially reasonable technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:</P>
            <UL items={[
              "Encryption of data in transit using TLS/SSL protocols",
              "Restricted access to personal data on a need-to-know basis",
              "Regular security assessments of our systems and processes",
              "Confidentiality agreements with team members who handle client data",
              "Secure, access-controlled data storage environments",
            ]} />
            <P>While we take security seriously and implement strong safeguards, no method of transmission or storage is 100% secure. We encourage you to use strong, unique passwords and exercise caution when sharing sensitive information online.</P>
            <P>In the event of a data breach that may affect your rights or freedoms, we will notify you and relevant authorities as required by applicable law.</P>
          </Section>

          <Section id="data-retention" title="10. Data Retention">
            <P>We retain personal information only as long as necessary for the purposes described in this policy, unless a longer retention period is required or permitted by law. Specifically:</P>
            <UL items={[
              "Contact inquiries and project communications: retained for 3 years after last interaction",
              "Client project data: retained for the duration of the engagement plus 5 years for legal and business purposes",
              "Billing and financial records: retained for 7 years as required by tax and accounting regulations",
              "Website analytics data: retained for 13 months, after which it is aggregated or deleted",
              "Chat and support records: retained for 2 years after resolution",
            ]} />
            <P>You may request deletion of your personal information at any time (subject to legal obligations) by contacting us at {EMAIL}.</P>
          </Section>

          <Section id="your-rights" title="11. Your Privacy Rights">
            <P>Depending on your location, you may have the following rights regarding your personal information:</P>
            <UL items={[
              "Right to Access: Request a copy of the personal information we hold about you",
              "Right to Correction: Request correction of inaccurate or incomplete information",
              "Right to Deletion: Request deletion of your personal information, subject to legal obligations",
              "Right to Restriction: Request that we limit the processing of your information in certain circumstances",
              "Right to Data Portability: Receive your data in a structured, machine-readable format",
              "Right to Object: Object to certain types of processing, including for marketing purposes",
              "Right to Withdraw Consent: Where processing is based on consent, withdraw it at any time",
            ]} />
            <P>To exercise any of these rights, please contact us at {EMAIL} or {PHONE}. We will respond to verified requests within 30 days (or as required by applicable law).</P>
          </Section>

          <Section id="california" title="12. California Residents — CCPA Rights">
            <P>If you are a California resident, you have the following rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):</P>
            <UL items={[
              "Right to Know: What personal information we collect, use, disclose, and sell (we do not sell)",
              "Right to Delete: Request deletion of your personal information we have collected",
              "Right to Opt-Out of Sale: We do not sell personal information — this right is satisfied by default",
              "Right to Non-Discrimination: We will not discriminate against you for exercising your CCPA rights",
              "Right to Correct: Request correction of inaccurate personal information",
              "Right to Limit Use of Sensitive Personal Information: We collect minimal sensitive data and only as necessary for services",
            ]} />
            <Highlight>
              We do not sell California consumers' personal information and have not done so in the
              preceding 12 months. We do not share personal information for cross-context behavioral
              advertising purposes.
            </Highlight>
            <P>To submit a verifiable consumer request, contact us at {EMAIL} with "California Privacy Request" in the subject line.</P>
          </Section>

          <Section id="canada" title="13. Canadian Residents — PIPEDA">
            <P>For clients and visitors in Canada, we comply with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy laws. Under PIPEDA, you have the right to:</P>
            <UL items={[
              "Know what personal information we hold about you and how it is used",
              "Access your personal information and request corrections",
              "Withdraw consent to the collection, use, or disclosure of your information",
              "File a complaint with the Office of the Privacy Commissioner of Canada if you believe your privacy rights have been violated",
            ]} />
            <P>We collect personal information only with your knowledge and consent, and only for purposes a reasonable person would consider appropriate in the circumstances.</P>
          </Section>

          <Section id="children" title="14. Children's Privacy">
            <P>
              Our website and services are not directed to individuals under the age of 18. We do not
              knowingly collect personal information from children under 18. If we become aware that we
              have collected personal information from a child without parental consent, we will promptly
              delete that information. If you believe we have collected information from a minor, please
              contact us immediately at {EMAIL}.
            </P>
          </Section>

          <Section id="changes" title="15. Changes to This Privacy Policy">
            <P>
              We may update this Privacy Policy from time to time to reflect changes in our practices,
              technology, legal requirements, or other factors. When we make material changes, we will:
            </P>
            <UL items={[
              "Update the \"Last Updated\" date at the top of this page",
              "Post the revised policy on our website at " + WEBSITE + "/privacy-policy",
              "Notify active clients by email if the changes significantly affect how we handle their data",
            ]} />
            <P>Your continued use of our website or services after the effective date of any changes constitutes your acceptance of the updated policy. We encourage you to review this page periodically.</P>
          </Section>

          <Section id="contact" title="16. Contact Us">
            <P>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</P>
            <div className="mt-4 p-5 rounded-xl border border-white/8 bg-white/[0.02] space-y-3">
              <div className="flex items-center gap-3 text-slate-300">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-violet-400 flex-shrink-0">
                  <path d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
                <span className="font-semibold">{COMPANY}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-violet-400 flex-shrink-0">
                  <path d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
                <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors">{EMAIL}</a>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-violet-400 flex-shrink-0">
                  <path d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25z" />
                </svg>
                <a href={`tel:+12018469830`} className="hover:text-white transition-colors">{PHONE}</a>
              </div>
              <div className="flex items-start gap-3 text-slate-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5">
                  <path d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" /><path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0z" />
                </svg>
                <div className="flex flex-col gap-0.5">
                  <span>Texas, United States</span>
                  <span>Karachi, Pakistan</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-violet-400 flex-shrink-0">
                  <path d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253" />
                </svg>
                <a href={WEBSITE} className="hover:text-white transition-colors">{WEBSITE}</a>
              </div>
            </div>
            <P>We aim to respond to all privacy-related requests within 30 days. For urgent matters, please call us directly.</P>
          </Section>

        </div>
      </div>
    </SharedLayout>
  );
}
